import { useState } from "react";
import ExcelJS from "exceljs";
import { asLocalDate } from "@freightsimple/time/branded-types";
import { formatLocalDate, today } from "@freightsimple/time/helpers";
import { FileXlsIcon } from "@phosphor-icons/react/FileXls";
import type { Invoice } from "@freightsimple/generated-dashboard-openapi-client/models/Invoice";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import { getShipmentDisplayDetails } from "@/Shared/Invoices/Helpers/invoiceUtils";
import { useAuthentication } from "@/Slices/authenticationSlice";
import { Button } from "@freightsimple/ui-core/Controls/Button";

function downloadBlob(blob: Blob, filename: string) {
  // Create a URL for the blob
  const url = window.URL.createObjectURL(blob);

  // Create a temporary link element
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;

  // Append to document, click, and cleanup
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Release the URL object
  window.URL.revokeObjectURL(url);
}

interface DownloadInvoicesExcelButtonProps {
  invoices: Invoice[];
  shipmentMap: Map<string, Shipment>;
}

export function PaymentsDownloadInvoicesExcelButton({
  invoices,
  shipmentMap,
}: DownloadInvoicesExcelButtonProps) {
  const { user } = useAuthentication();
  const companyName = user?.companyName ?? "";
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadExcel = async () => {
    setIsDownloading(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Invoices");

      worksheet.columns = [
        { header: "Invoice Type", key: "invoiceType", width: 20 },
        { header: "Invoice Identifier", key: "invoiceIdentifier", width: 32 },
        { header: "Created At", key: "createdAt", width: 20 },
        { header: "Due Date", key: "dueDate", width: 20 },
        { header: "Pro Number", key: "proNumber", width: 20 },
        { header: "From", key: "from", width: 30 },
        { header: "To", key: "to", width: 30 },
        { header: "Customer", key: "customer", width: 30 },

        { header: "Total Amount", key: "totalAmount", width: 20 },
        { header: "Currency", key: "currency", width: 10 },
      ];

      invoices.forEach((invoice) => {
        const shipment = shipmentMap.get(invoice.shipmentId);

        const details = getShipmentDisplayDetails(shipment, true);

        const pickupRef = details.pickupReferenceNumber
          ? `, ${details.pickupReferenceNumber}`
          : "";
        const deliveryRef = details.deliveryReferenceNumber
          ? `, ${details.deliveryReferenceNumber}`
          : "";

        worksheet.addRow({
          invoiceType: invoice.invoiceType,
          invoiceIdentifier: invoice.invoiceIdentifier,
          createdAt: invoice.invoiceDate
            ? formatLocalDate(asLocalDate(invoice.invoiceDate), "YYYY-MM-DD")
            : undefined,
          dueDate: invoice.dueDate,
          proNumber: shipment?.proNumber,
          from: `${details.pickupBusinessName}${pickupRef} ${details.pickupCity}`.trim(),
          to: `${details.deliveryBusinessName}${deliveryRef} ${details.deliveryCity}`.trim(),
          customer: companyName,
          totalAmount: `$${invoice.amount.toFixed(2)}`,
          currency: invoice.currency,
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const filename = `invoices_${today()}.xlsx`;
      downloadBlob(blob, filename);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      onClick={downloadExcel}
      icon={FileXlsIcon}
      disabled={isDownloading}
      disabledMessage="Download is in progress"
      loading={isDownloading}
    >
      Download Excel
    </Button>
  );
}
